import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const isProduction =
    window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
const API_URL = isProduction ? import.meta.env.VITE_API_URL : 'http://localhost:3000';

/**
 * Fetches the list of distinct group names scoped to the current organization
 * and, when provided, the active project.
 * Used to populate the group filter combobox in FilterBar.
 * Groups named "__ungrouped__" (server-side synthetic bucket) are excluded.
 */
export function useGroupNames(projectId?: string | null): string[] {
    const { token } = useAuth();

    const { data } = useQuery<string[]>({
        queryKey: ['group-names', token, projectId ?? null],
        queryFn: async () => {
            const params = new URLSearchParams({ limit: '100' });
            if (projectId) params.set('projectId', projectId);

            const { data } = await axios.get(
                `${API_URL}/api/executions/grouped?${params.toString()}`,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            if (data.success && data.data?.groups) {
                return (data.data.groups as Array<{ groupName: string }>)
                    .map((g) => g.groupName)
                    .filter((n) => n !== '__ungrouped__');
            }
            return [];
        },
        enabled: !!token,
        staleTime: 30_000,
    });

    return data ?? [];
}
